import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getEnvironment } from "./utils/getEnvironment";
import { RouterProvider } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/uk";
import { ThemeProvider, createTheme } from "@mui/material";
import router from "./router";
import { useCookies } from "react-cookie";
import useWeglot from "./hooks/useWeglot";
import { PWAProvider } from "./contexts/PWAContext";
import usePushNotificationPermission from "./hooks/usePushNotificationPermissions";
import { Login } from "./components/Login";
import AppWrapper from "./components/AppWrapper";
import { useAppSetup } from "./hooks/useAppSetup";
import useBrandUI from "./hooks/useBrandUI";
import { FullScreenLoader } from "./components/FullScreenLoader";

const environment = getEnvironment();

const Main = () => {
    useWeglot();
    const [cookies] = useCookies(["uuid", "theme_color"]);
    const { uuid, theme_color } = cookies;

    const publicRoutes = ["/login", "/forgot-password", "/health/check"];
    const isPublicRoute = publicRoutes.some((r) =>
        window.location.pathname.startsWith(r)
    );
    const allowedPartnerRoutes = ["/partner", "/menu/password", "/menu/email"];
    const isAllowedPartnerRoute = allowedPartnerRoutes.some((r) =>
        window.location.pathname.startsWith(r)
    );

    const {
        authReady,
        needsLogin,
        appReady,
        //needsSetup,
        needsSubscription,
        subscriptionExpired,
        brand,
    } = useAppSetup();

    useBrandUI(brand);

    usePushNotificationPermission(uuid, environment.vapid_key);

    const theme = useMemo(() => {
        const appTheme = theme_color ?? "#8B6969";
        return createTheme({
            components: {
                MuiButton: {
                    defaultProps: {
                        className: "TestMuiButton",
                    },
                },
            },
            palette: {
                primary: {
                    main: appTheme,
                },
                secondary: {
                    main: "#FFFFFF",
                },
                info: {
                    main: appTheme + "33",
                },
                warning: {
                    main: "#ED6C02",
                },
                success: {
                    main: "#4CAF50",
                },
            },
            shadows: [
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
            ],
        });
    }, [theme_color]);

    // Check if the current URL matches /redirect/:uuid
    const isRedirecting = window.location.pathname.startsWith("/redirect/");

    if (isRedirecting) {
        return (
            <ThemeProvider theme={theme}>
                <AppWrapper>
                    <RouterProvider router={router} />
                </AppWrapper>
            </ThemeProvider>
        );
    }

    if (!authReady && !isPublicRoute) return <FullScreenLoader />;
    if (needsLogin && !isPublicRoute) {
        return (
            <ThemeProvider theme={theme}>
                <AppWrapper>
                    <Login />
                </AppWrapper>
            </ThemeProvider>
        );
    }

    if (!appReady && !isPublicRoute) return <FullScreenLoader />;

    const getRedirectionPath = () => {
        if (!authReady) return null;
        //if (needsLogin) return "/login";
        if (!appReady) return null;

        // Partner brands: force to /partner and bypass all checks
        if (brand?.isPartner) {
            return isAllowedPartnerRoute ? null : "/partner";
        }

        //if (needsSetup && !window.location.pathname.startsWith("/setup"))
        //    return "/setup/services";
        if (
            //!needsSetup && // 👈 suppress if setup isn't done
            needsSubscription &&
            !window.location.pathname.startsWith("/menu/subscriptions") &&
            !window.location.pathname.startsWith("/setup")
        )
            return "/menu/subscriptions";
        if (
            //!needsSetup &&
            subscriptionExpired &&
            !window.location.pathname.startsWith("/expired") &&
            !window.location.pathname.startsWith("/menu/subscriptions") &&
            !window.location.pathname.startsWith("/setup")
        )
            return "/expired";
        return null;
    };
    const redirectPath = getRedirectionPath();

    if (redirectPath) {
        window.location.replace(redirectPath);
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            <PWAProvider appInitialized={appReady}>
                <AppWrapper>
                    <RouterProvider router={router} />
                </AppWrapper>
            </PWAProvider>
        </ThemeProvider>
    );
};

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);
root.render(
    <>
        <GoogleOAuthProvider clientId={environment.google_client_id}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="uk">
                <Main />
            </LocalizationProvider>
        </GoogleOAuthProvider>
    </>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
